import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerITyskland() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" },
    { name: "Trailer i Tyskland", path: "/trailer-i-tyskland" }
  ];
  const tocItems = [
    { id: "hastighed-tyskland", title: "Hastighedsgrænser med trailer i Tyskland" },
    { id: "tysk-tempo-100", title: "Tysk Tempo 100 på Autobahn" },
    { id: "soendagsforbud", title: "Søndagskørselsforbud (Sonn- und Feiertagsfahrverbot)" },
    { id: "sikkerhedswire-krav", title: "Særligt krav til sikkerhedswire (Holland-øje)" },
    { id: "udstyr-i-bilen", title: "Lovpligtigt sikkerhedsudstyr i Tyskland" }
  ];

  const faq = [
    {
      q: "Gælder et dansk Tempo 100-mærke i Tyskland?",
      a: "Nej. Et dansk Tempo 100-mærke er ikke gyldigt på de tyske motorveje. Har du kun dansk Tempo 100, må du højst køre 80 km/t på Autobahn i Tyskland. For at køre 100 km/t skal traileren godkendes til tysk Tempo 100 af en tysk kontrolinstans (som TÜV eller DEKRA)."
    },
    {
      q: "Må jeg køre med trailer i Tyskland om søndagen?",
      a: "Ja, hvis du kører i en almindelig personbil (M1) til private formål (f.eks. med feriebagage, campingvogn eller haveaffald). Søndagsforbuddet (kl. 00-22) gælder for lastbiler over 7,5 ton samt varebiler registreret som godstransport (LKW), der trækker en erhvervsmæssig trailer."
    },
    {
      q: "Må sikkerhedswiren lægges over trækkrogen i Tyskland?",
      a: "Nej, tysk politi udsteder store bøder, hvis en sikkerhedswire blot er slynget som en løkke over kuglehovedet. Wiren skal være fastgjort til et fastmonteret øje på trækket eller en særlig spændebøjle (ofte kaldet en Holland-klemme)."
    },
    {
      q: "Hvor hurtigt må man køre på tyske landeveje med trailer?",
      a: "På almindelig landevej (Außerorts) må et vogntog højst køre 80 km/t, såfremt vogntogets samlede tilladte totalvægt ikke overstiger 3.500 kg. Overstiger vogntoget 3.500 kg, er grænsen på landevej 60 km/t."
    }
  ];

  const sources = [
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" },
    { title: "Færdselsstyrelsen: Kørekort og kørsel i udlandet", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/koerekort-og-udland" },
    { title: "Bekendtgørelse om Tempo 100 for påhængskøretøjer (BEK nr. 1614 af 13/12/2016)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2016/1614" }
  ];

  return (
    <>
      <SEO 
        title="Trailer i Tyskland: Fartgrænser, søndagsforbud og regler" 
        description="Alt om kørsel med trailer i Tyskland: hastighed på Autobahn og landevej, tysk Tempo 100, søndagsforbud, lovpligtigt udstyr og krav til sikkerhedswire." 
        pathname="/trailer-i-tyskland"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailer i Tyskland: Hastighed, søndage og udstyr
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          I Tyskland er hastighedsgrænsen for en almindelig bil med trailer som udgangspunkt <strong>80 km/t på Autobahn</strong> og <strong>80 km/t på landevej</strong> (for vogntog op til 3.500 kg).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          For at køre 100 km/t på tyske motorveje kræves et specifikt tysk Tempo 100-mærke udstedt af f.eks. TÜV eller DEKRA – et dansk Tempo 100-mærke er ikke gyldigt. Desuden stiller det tyske politi strenge krav til fastgørelse af sikkerhedswiren i et fast øje og håndhæver et omfattende søndagskørselsforbud for erhvervsmæssige køretøjer.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="De 3 vigtigste faldgruber i Tyskland">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>1. Dansk Tempo 100 gælder <strong>ikke</strong> i Tyskland. Kør højst 80 km/t.</li>
            <li>2. Sikkerhedswire <strong>skal</strong> sidde i et fast øje på trækket (bøder op til 75 €).</li>
            <li>3. Erhvervsmæssig kørsel med varebil og trailer er <strong>forbudt om søndagen</strong>.</li>
          </ul>
        </Callout>

        <h2 id="hastighed-tyskland" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hastighedsgrænser med trailer i Tyskland
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I den tyske færdselslov (StVO § 3) differentieres der markant mellem almindelige vogntog og vogntog over 3.500 kg:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Vejtype</th>
                <th className="py-2.5 pr-4 font-semibold">Vogntog op til 3.500 kg</th>
                <th className="py-2.5 pr-4 font-semibold">Vogntog over 3.500 kg</th>
                <th className="py-2.5 font-semibold">Med tysk Tempo 100</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">I bymæssig bebyggelse (Innerorts)</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 text-[#5C636B]">50 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Landevej (Außerorts)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">60 km/t</td>
                <td className="py-3 text-[#5C636B]">80 km/t</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Motorvej (Autobahn / Kraftfahrstraße)</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">100 km/t</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="tysk-tempo-100" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Tysk Tempo 100 på Autobahn
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Ønsker du at køre 100 km/t gennem Tyskland, skal du have en tysk godkendelse (9. Ausnahmeverordnung zur StVO). Det kræver:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Dæk på traileren må højst være 6 år gamle og skal være godkendt til mindst 120 km/t (kategori L).</li>
          <li>Hydrauliske støddæmpere på traileren.</li>
          <li>Trækbil med ABS.</li>
          <li>Godkendelse og udlevering af det tyske seglmærke (med delstatens stempel) hos f.eks. en dansk synshal med TÜV/DEKRA-samarbejde eller ved et synskontor lige syd for grænsen.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om de tilsvarende danske krav under <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100 i Danmark</Link>.
        </p>

        <h2 id="soendagsforbud" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Søndagskørselsforbud (Sonn- und Feiertagsfahrverbot)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Tyskland gælder der ifølge færdselslovens § 30 et generelt kørselsforbud på alle søn- og helligdage mellem kl. 00:00 og 22:00.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          <strong>Hvem er omfattet?</strong> Forbuddet gælder for lastbiler over 7,5 ton samt for alle køretøjer indregistreret som varebil / godskøretøj (LKW på registreringsattesten), som trækker en trailer til erhvervsmæssig godstransport.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          <strong>Hvem er undtaget?</strong> Kørsel med almindelig personbil (M1 på registreringsattesten) med trailer er fuldt lovlig på søndage. Kørsel til rene sports- og fritidsformål (f.eks. hestetrailer til stævne, bådtrailer eller feriebagage) er ligeledes undtaget, selvom trækbilen er en varebil.
        </p>

        <h2 id="sikkerhedswire-krav" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Særligt krav til sikkerhedswire (Holland-øje)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark tolereres det ofte på ældre biler, at sikkerhedswiren lægges som en løkke over trækket. Dette er <strong>strengt forbudt i Tyskland</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Tysk politi kræver, at wiren er fastgjort gennem et fast svejset eller boltet øje i bilens anhængertræk. Har dit træk ikke et sådant øje, skal der monteres et godkendt spændebeslag (en spændekrave / Holland-øje) omkring trækstangen, hvori wirens karabinhage kan clipses fast. Bøden for manglende fastgørelse er op til 75 euro.
        </p>

        <h2 id="udstyr-i-bilen" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Lovpligtigt sikkerhedsudstyr i Tyskland
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du krydser den tyske grænse, skal du altid medbringe:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Advarselstrekant:</strong> Skal altid findes i bilen.</li>
          <li><strong>Refleksveste:</strong> Mindst én EN 471 / EN ISO 20471-godkendt advarselsvest placeret inde i kabinen (ikke i bagagerummet eller traileren).</li>
          <li><strong>Forbindskasse:</strong> Lovpligtig førstehjælpstaske i henhold til DIN 13164 med gyldig holdbarhedsdato på sterile komponenter.</li>
          <li><strong>Original registreringsattest:</strong> Del 1 for både trækbil og trailer.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Se også vores oversigter over <Link to="/trailer-i-sverige" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Sverige</Link>, <Link to="/trailer-i-udlandet" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i resten af Europa</Link> og <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">danske hastighedsgrænser</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
